import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import listPlugin from "@fullcalendar/list"
import { DatesSetArg, EventClickArg, EventContentArg } from "@fullcalendar/core"
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction"
import { DayGridContent } from "./DayGridContent"
import { InlineFallbackContents } from "../errors/InlineErrorBoundary"
import { ErrorBoundary } from "react-error-boundary"
import { TimeGridContent } from "./TimeGridContent"
import { AdditionalEventMenuItem } from "../../../modules/nonAdminUser/reports/home/components/upcomingOrders/meetings/MeetingCardMenu"
import { CalendarEvent } from "./calendarTypes"
import { useMemo } from "react"
import { MenuCalendarEvent } from "../../../modules/merchant/merchant/menuPlanning/pages/MenuCalendar"
import { selectSelectedLocale } from "../../../modules/localization/localizationSelectors"
import { useAppSelector } from "../../../modules/store/storeHooks"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { useIsMobile } from "../../hooks/useIsMobile"
import { getFullCalendarLocale } from "../../../modules/localization/localizationLib"

type StyleProps = Readonly<{
    isEventClick: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { isEventClick }) => ({
    container: {
        width: "100%",

        "& .fc": {
            "& button, & .fc-toolbar-chunk:last-child button": {
                backgroundColor: theme.palette.tailwind.sky[700],
                color: "white",
            },
            "& button:hover, & .fc-toolbar-chunk:last-child button:hover": {
                backgroundColor: theme.palette.tailwind.sky[800],
            },
            "& button:active, & .fc-toolbar-chunk:last-child .fc-button-active": {
                backgroundColor: theme.palette.tailwind.sky[900],
            },
        },
        ".fc-event:hover": {
            cursor: isEventClick ? "pointer" : "default",
        },
        ".fc-daygrid-dot-event:hover": {
            background: "inherit",
        },
        ".fc-v-event": {
            background: "none",
            border: "none",
        },
        ".fc-event-main > div": {
            height: "100%",
        },
        ".fc-event-main > div > div": {
            height: "fit-content",
        },
        [theme.breakpoints.down("sm")]: {
            "& .fc .fc-toolbar-title": {
                fontSize: "1.125rem",
            },
            // the event card already shows the time and the colour
            "& .fc .fc-list-event-time, & .fc .fc-list-event-graphic": {
                display: "none",
            },
            "& .fc .fc-list-table td": {
                padding: ".25rem .5rem",
            },
        },
    },
    errorBoundary: {
        background: theme.palette.tailwind.slate[50],
        padding: "10%",
    },
}))

const DESKTOP_HEADER_TOOLBAR = {
    left: "prev,next today",
    center: "title",
    right: "dayGridMonth,timeGridWeek,timeGridDay",
}
const MOBILE_HEADER_TOOLBAR = { left: "title", right: "prev,next" }
const MOBILE_FOOTER_TOOLBAR = { left: "today", right: "listWeek,dayGridMonth" }

type StandardCalendarProps = Readonly<{
    events: CalendarEvent[] | MenuCalendarEvent[]
    additionalMenuItems?: AdditionalEventMenuItem[]
    handleEventClick?: (arg: EventClickArg) => void
    handleDatesSet: (arg: DatesSetArg) => void
    handleDateClick?: ((arg: DateClickArg) => void) | undefined
    onChatOpen?: (orderId: number, orderTitle: string) => void
    //
    dayGrid?: ((arg: EventContentArg) => JSX.Element) | null
    timeGrid?: ((arg: EventContentArg) => JSX.Element) | null
}>

export function StandardCalendar({
    additionalMenuItems,
    events,
    handleEventClick,
    handleDatesSet,
    handleDateClick,
    onChatOpen,
    dayGrid,
    timeGrid,
}: StandardCalendarProps) {
    const { classes } = useStyles({ isEventClick: !!handleEventClick })
    const isMobile = useIsMobile()
    const locale = useAppSelector(selectSelectedLocale)
    const { l10n } = useAppLocalization()

    const fullCalendarLocale = useMemo(() => getFullCalendarLocale(locale), [locale])

    const defaultDayGridEventContent = (arg: EventContentArg) => (
        <ErrorBoundary
            fallback={
                <InlineFallbackContents
                    errorMessage={l10n.getString("inline-error-boundary-calendar-event-message")}
                    externalClasses={classes.errorBoundary}
                />
            }
        >
            {/* strictly use CalendarEvent as it's default view */}
            <DayGridContent
                events={events as CalendarEvent[]}
                additionalMenuItems={additionalMenuItems}
                event={arg.event}
                onChatOpen={onChatOpen}
            />
        </ErrorBoundary>
    )

    const defaultTimeGridEventContent = (arg: EventContentArg) => (
        <ErrorBoundary
            fallback={
                <InlineFallbackContents
                    errorMessage={l10n.getString("inline-error-boundary-calendar-event-message")}
                    externalClasses={classes.errorBoundary}
                />
            }
        >
            {/* strictly use CalendarEvent as it's default view */}
            <TimeGridContent events={events as CalendarEvent[]} event={arg.event} onChatOpen={onChatOpen} />
        </ErrorBoundary>
    )

    const views = {
        ...(dayGrid !== null && {
            dayGrid: {
                eventContent: dayGrid ?? defaultDayGridEventContent,
            },
            list: {
                eventContent: dayGrid ?? defaultDayGridEventContent,
            },
        }),
        ...(timeGrid !== null && {
            timeGrid: {
                eventContent: timeGrid ?? defaultTimeGridEventContent,
            },
        }),
    }

    const baseEvents = useMemo(() => events.map((event) => ({ ...event, id: event.id.toString() })), [events])

    return (
        <Box className={classes.container}>
            <FullCalendar
                // initialView only applies on mount
                key={isMobile ? "mobile" : "desktop"}
                plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                locale={fullCalendarLocale}
                headerToolbar={isMobile ? MOBILE_HEADER_TOOLBAR : DESKTOP_HEADER_TOOLBAR}
                footerToolbar={isMobile ? MOBILE_FOOTER_TOOLBAR : false}
                views={views}
                initialView={isMobile ? "listWeek" : "dayGridMonth"}
                events={baseEvents}
                themeSystem="bootstrap"
                eventClick={handleEventClick}
                datesSet={handleDatesSet}
                dateClick={handleDateClick}
            />
        </Box>
    )
}
